import type { SRT_RowSelectionState } from '@/shared/components/master-table'
import type { ApplyAction, SettingsTemplateModel } from '@/shared/generated'

import React, { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { any, indexBy, prop } from 'lodash/fp'
import { CheckIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { usePricingCampaignsPickerTable } from '@/features/select-pricing-campaigns'

import {
	getOptimizationsMap,
	getOptStatus,
	useOptimizationsQuery,
} from '@/entities/optimization'
import {
	useOptimizationGroupsQuery,
	useWithAffectedOGsHandler,
} from '@/entities/optimization-group'
import {
	getPricingCampaignEngine,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'
import { useApplySettingsTemplateMutation } from '@/entities/settings-template'

import { MasterTable, SRT_TableToolbar } from '@/shared/components/master-table'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import { Loader } from '@/shared/components/ui/loader'
import {
	ModalBody,
	ModalClose,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'

export function ApplyToPricingCampaigns({
	template,
	close,
	action,
}: {
	template: SettingsTemplateModel
	close: () => void
	action: ApplyAction
}) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const [rowSelection, setRowSelection] = React.useState<SRT_RowSelectionState>(
		{}
	)
	const optimizationGroupsQuery = useOptimizationGroupsQuery()
	const pricingCampaignsQuery = usePricingCampaignsQuery({})
	const optimizationsQuery = useOptimizationsQuery({
		select: getOptimizationsMap,
	})
	const baseApplyMutation = useApplySettingsTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['optimizations'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaign'],
			})
			toast.success(t('toast.entity.applied', { entity: t('entity.template') }))
			close()
		},
	})
	const applyMutation = useWithAffectedOGsHandler(baseApplyMutation)
	const isLoading = any(prop('isLoading'), [
		pricingCampaignsQuery,
		optimizationGroupsQuery,
		optimizationsQuery,
	])
	const data = useMemo(() => {
		const ogMap = indexBy('id', optimizationGroupsQuery.data)

		return (
			pricingCampaignsQuery.data?.map((pc) => ({
				id: pc.id,
				name: pc.name,
				engine: getPricingCampaignEngine(pc),
				optimization_group_id: pc.optimization_group_id!,
				optimization_group_name: pc.optimization_group_id
					? ogMap?.[pc.optimization_group_id]?.name
					: undefined,
				optimization_status: pc.optimization_group_id
					? getOptStatus(
							optimizationsQuery.data?.[pc.optimization_group_id]?.status
						)
					: undefined,
			})) || []
		)
	}, [optimizationsQuery, optimizationGroupsQuery.data, pricingCampaignsQuery])
	const canGoNext = useMemo(
		() => Object.keys(rowSelection).length > 0,
		[rowSelection]
	)
	const handleApply = useCallback(async () => {
		const answer = await confirm({
			title: t('template.apply.confirm.title', {
				defaultValue: 'Apply settings template',
			}),
			description: t('template.apply.confirm.text', {
				defaultValue: 'Are you sure you want to apply the template',
			}),
			actionText: t('app.apply', { defaultValue: 'Apply' }),
			cancelText: t('general_cancel'),
		})
		if (answer) {
			applyMutation.mutate({
				id: template.id,
				data: {
					ids: Object.keys(rowSelection),
					action,
				},
			})
		}
	}, [rowSelection, action, applyMutation, template.id, t])
	const options = useMemo(
		() => ({
			optimization_group_name:
				optimizationGroupsQuery.data?.map((og) => ({
					label: og.name,
					value: og.name,
				})) || [],
		}),
		[optimizationGroupsQuery.data]
	)
	const table = usePricingCampaignsPickerTable({
		isLoading: isLoading || applyMutation.isPending,
		options,
		data,
		rowSelection,
		columnVisibility: {
			products_count: false,
		},
		setRowSelection,
	})

	return (
		<>
			<Blanket
				isTinted={isLoading || applyMutation.isPending}
				className='absolute flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-xs dark:bg-black/30'
			>
				<Loader />
			</Blanket>
			<ModalHeader>
				<ModalTitle>
					{t('template.apply.modal.title', {
						name: template.name,
						defaultValue: `Apply settings template: ${template.name}`,
					})}
				</ModalTitle>
				<ModalDescription>
					{t('template.apply.modal.subtitle', {
						defaultValue:
							'This will apply the settings from the template to the current settings. Are you sure you want to apply the template',
					})}
				</ModalDescription>
			</ModalHeader>
			<ModalBody>
				{applyMutation.isError && (
					<Alert variant='destructive' className='mb-2'>
						<AlertDescription>
							{t('template.apply.error', {
								defaultValue:
									'We could not apply the template. Please ensure that the selected items are in a valid state, or try again later.',
							})}
						</AlertDescription>
					</Alert>
				)}
				<div className='pb-2'>
					<SRT_TableToolbar table={table} />
				</div>
				<div className='border-base flex h-96 flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
					<MasterTable table={table} />
				</div>
			</ModalBody>
			<ModalFooter className='flex justify-between'>
				<ModalClose asChild>
					<Button data-testid='apply-template-modal-cancel'>
						{t('general_cancel')}
					</Button>
				</ModalClose>
				<Button
					data-testid='apply-template-modal-apply'
					variant='primary'
					disabled={!canGoNext || applyMutation.isPending}
					onClick={handleApply}
				>
					<CheckIcon />
					{t('general_apply')}
				</Button>
			</ModalFooter>
		</>
	)
}
